import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { UserCog, Plus } from "lucide-react";

import {
  Box,
  Paper,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Chip,
} from "@mui/material";

import DataTable from "@/components/mui/DataTable";

export default function Users() {
  const [users, setUsers] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);

  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  // paginação (MUI + backend Laravel)
  const [page, setPage] = useState(0); // zero-based no front
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: "",
    // compatível com enum ['system_admin', 'company_supervisor', 'company_user']
    role: "company_user",
  });

  useEffect(() => {
    fetchUsers(0, rowsPerPage);
  }, []);

  const fetchUsers = async (pageIndex = 0, perPage = rowsPerPage) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/users`, {
        params: {
          page: pageIndex + 1, // Laravel espera 1-based
          per_page: perPage,
        },
      });

      const data = response.data;
      if (data && data.data) {
        // resposta paginada padrão Laravel
        setUsers(data.data);
        setTotalUsers(data.total || data.data.length);
      } else {
        // fallback caso não esteja paginado
        const list = data || [];
        setUsers(list);
        setTotalUsers(list.length);
      }
    } catch (error) {
      toast.error("Erro ao carregar usuários");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        name: user.name || "",
        email: user.email || "",
        password: "",
        role: user.role || "company_user",
      });
    } else {
      setEditingUser(null);
      resetForm();
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingUser(null);
    resetForm();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        const dataToSend = { ...formData };

        // se estiver editando um system_admin, não deixamos alterar o role via UI
        if (editingUser.role === "system_admin") {
          dataToSend.role = "system_admin";
        }

        if (!formData.password) {
          delete dataToSend.password;
        }

        await axios.put(`${API}/users/${editingUser.id}`, dataToSend);
        toast.success("Usuário atualizado!");
      } else {
        // criação: NUNCA permitir criar system_admin pela UI
        const payload = { ...formData };
        if (payload.role === "system_admin") {
          payload.role = "company_user";
        }
        await axios.post(`${API}/auth/register`, payload);
        toast.success("Usuário criado!");
      }

      handleCloseModal();
      fetchUsers(page, rowsPerPage);
    } catch (error) {
      toast.error(
        error.response?.data?.detail ||
        error.response?.data?.message ||
        "Erro ao salvar usuário"
      );
    }
  };

  const toggleUserStatus = async (userId, currentStatus) => {
    try {
      await axios.put(`${API}/users/${userId}`, { active: !currentStatus });
      toast.success("Status atualizado!");
      fetchUsers(page, rowsPerPage);
    } catch (error) {
      toast.error("Erro ao atualizar status");
    }
  };

  const resetForm = () => {
    setFormData({
      email: "",
      password: "",
      name: "",
      role: "company_user",
    });
  };

  const getRoleChipProps = (role) => {
    switch (role) {
      case "system_admin":
        return { label: "Administrador do Sistema", color: "error" };
      case "company_supervisor":
        return { label: "Supervisor", color: "warning" };
      case "company_user":
      default:
        return { label: "Usuário", color: "info" };
    }
  };

  const handleChangePage = (_event, newPage) => {
    setPage(newPage);
    fetchUsers(newPage, rowsPerPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRows = parseInt(event.target.value, 10);
    setRowsPerPage(newRows);
    setPage(0);
    fetchUsers(0, newRows);
  };

  if (loading && users.length === 0) {
    return (
      <Box
        sx={{
          minHeight: 200,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // ===== columns do DataTable =====
  const columns = [
    {
      id: "name",
      label: "Nome",
      render: (user) => (
        <Typography fontWeight={600}>{user.name}</Typography>
      ),
    },
    {
      id: "email",
      label: "Email",
    },
    {
      id: "role",
      label: "Perfil",
      render: (user) => {
        const { label, color } = getRoleChipProps(user.role);
        return <Chip label={label} color={color} size="small" />;
      },
    },
    {
      id: "status",
      label: "Status",
      render: (user) => (
        <Chip
          label={user.active ? "Ativo" : "Inativo"}
          color={user.active ? "success" : "error"}
          size="small"
        />
      ),
    },
    {
      id: "actions",
      label: "Ações",
      align: "right",
      render: (user) => (
        <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => handleOpenModal(user)}
          >
            Editar
          </Button>
          <Button
            variant="contained"
            size="small"
            color={user.active ? "error" : "success"}
            onClick={() => toggleUserStatus(user.id, user.active)}
            data-testid={`toggle-user-${user.id}`}
          >
            {user.active ? "Desativar" : "Ativar"}
          </Button>
        </Box>
      ),
    },
  ];

  const tablePagination = {
    page,
    rowsPerPage,
    total: totalUsers,
    onPageChange: handleChangePage,
    onRowsPerPageChange: handleChangeRowsPerPage,
    rowsPerPageOptions: [5, 10, 25, 50],
  };

  return (
    <Box data-testid="users-page">
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} mb={4}>
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Usuários
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Controle de usuários cadastrados no sistema
          </Typography>
        </Box>

        <Button
          variant="contained"
          startIcon={<Plus size={16} />}
          onClick={() => handleOpenModal(null)}
          data-testid="add-user-btn"
        >
          Novo Usuário
        </Button>
      </Box>

      <Paper className="data-table-container">
        {/* Tabela ou estado vazio */}
        {users.length === 0 ? (
          <Box
            sx={{
              py: 6,
              px: 3,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              gap: 1.5,
            }}
          >
            <UserCog size={64} />
            <Typography variant="h6">
              Nenhum usuário cadastrado
            </Typography>
            <Typography variant="body2">
              Adicione usuários para acessar o sistema
            </Typography>
          </Box>
        ) : (
          <DataTable
            columns={columns}
            rows={users}
            loading={loading}
            pagination={tablePagination}
            getRowId={(row) => row.id}
            getRowTestId={(row) => `user-row-${row.id}`}
          />
        )}
      </Paper>

      {/* Dialog de criação/edição */}
      <Dialog
        open={showModal}
        onClose={handleCloseModal}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {editingUser ? "Editar Usuário" : "Novo Usuário"}
        </DialogTitle>
        <DialogContent dividers>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                label="Nome"
                fullWidth
                required
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                data-testid="user-name-input"
              />

              <TextField
                label="Email"
                type="email"
                fullWidth
                required
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                data-testid="user-email-input"
              />

              <TextField
                label={
                  editingUser
                    ? "Senha (deixe em branco para manter)"
                    : "Senha"
                }
                type="password"
                fullWidth
                required={!editingUser}
                value={formData.password}
                onChange={(e) =>
                  setFormData({ ...formData, password: e.target.value })
                }
                data-testid="user-password-input"
              />

              {/* Perfil */}
              {editingUser && editingUser.role === "system_admin" ? (
                // Para Administrador do Sistema, não permite alterar via UI
                <TextField
                  label="Perfil"
                  value="Administrador do Sistema"
                  fullWidth
                  size="small"
                  disabled
                />
              ) : (
                <FormControl fullWidth size="small">
                  <InputLabel id="user-role-label">Perfil *</InputLabel>
                  <Select
                    labelId="user-role-label"
                    label="Perfil"
                    value={formData.role}
                    onChange={(e) =>
                      setFormData({ ...formData, role: e.target.value })
                    }
                    required
                    data-testid="user-role-select"
                  >
                    {/* NÃO mostramos system_admin no Select */}
                    <MenuItem value="company_user">Usuário</MenuItem>
                    <MenuItem value="company_supervisor">Supervisor</MenuItem>
                  </Select>
                </FormControl>
              )}
            </Box>

            <DialogActions sx={{ mt: 3 }}>
              <Button
                type="button"
                variant="outlined"
                onClick={handleCloseModal}
                data-testid="cancel-user-btn"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                variant="contained"
                data-testid="submit-user-btn"
              >
                {editingUser ? "Atualizar" : "Criar"}
              </Button>
            </DialogActions>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
